import { useState } from "react";

/**
 * Custom hook to manage chart data and visualization settings
 * @param {Function} getCategoryName - Function to get category name from ID
 * @returns {Object} Chart state and data processing functions
 */
const useCharts = (getCategoryName) => {
  // State for chart visualization settings
  const [activeTab, setActiveTab] = useState(0);
  const [chartType, setChartType] = useState("bar");

  // Chart colors for consistent styling
  const CHART_COLORS = [
    "#3f51b5",
    "#f44336",
    "#4caf50",
    "#ff9800",
    "#2196f3",
    "#9c27b0",
    "#009688",
    "#ffeb3b",
    "#795548",
    "#607d8b",
  ];

  /**
   * Handles tab change
   * @param {Event} event - The event object
   * @param {number} newValue - New tab index
   */
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  /**
   * Changes chart visualization type
   * @param {string} type - Chart type (bar, line, pie)
   */
  const handleChartTypeChange = (type) => {
    setChartType(type);
  };

  /**
   * Processes category summary data for charts
   * @param {Array} categorySummary - Raw category summary data
   * @returns {Array} Processed data for charts
   */
  const getCategoryChartData = (categorySummary) => {
    if (!categorySummary || categorySummary.length === 0) return [];

    return categorySummary.map((item) => {
      const name = item.category__name || getCategoryName(item.category);
      return {
        name: name || "Unidentified Category",
        value: item.totalValue || 0,
      };
    });
  };

  /**
   * Processes weekly summary data for charts
   * @param {Array} weeklySummary - Raw weekly summary data
   * @returns {Array} Processed data for charts
   */
  const getWeeklyChartData = (weeklySummary) => {
    if (!weeklySummary || weeklySummary.length === 0) return [];

    return weeklySummary.map((item) => {
      return {
        name: `Week ${item.week_number || "?"}`,
        value: item.totalValue || 0,
      };
    });
  };

  /**
   * Creates a customized pie chart label renderer
   * @returns {Function} Label renderer function
   */
  const createCustomizedPieLabel = () => {
    return ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
      const RADIAN = Math.PI / 180;
      const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
      const x = cx + radius * Math.cos(-midAngle * RADIAN);
      const y = cy + radius * Math.sin(-midAngle * RADIAN);

      return percent > 0.05 ? (
        <text
          x={x}
          y={y}
          fill="white"
          textAnchor="middle"
          dominantBaseline="central"
        >
          {`${(percent * 100).toFixed(0)}%`}
        </text>
      ) : null;
    };
  };

  return {
    activeTab,
    chartType,
    CHART_COLORS,
    handleTabChange,
    handleChartTypeChange,
    getCategoryChartData,
    getWeeklyChartData,
    renderCustomizedPieLabel: createCustomizedPieLabel(),
  };
};

export default useCharts;
